import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DeviceSummary } from '@ghostscreen/contracts';
import { StatusPill } from '../components/dashboard/StatusPill';
import { PlatformPill } from '../components/dashboard/PlatformPill';
import { formatDeviceHardware } from '../lib/device/formatDeviceHardware';
import { QueryEmptyState } from '../components/dashboard/QueryEmptyState';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { StatCard } from '../components/dashboard/StatCard';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { deriveFleetCounts } from '../lib/dashboard/deriveDisplayStats';
import { formatRelativeTime } from '../lib/dashboard/relativeTime';
import { formatDeviceDisplaySummary } from '../lib/device/formatDeviceDisplaySummary';
import { formatCount } from '../lib/dashboard/formatCount';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { getScreenConnectionStatus } from '../lib/screen-health';
import { useOrganisationId } from '../lib/session';
import { DeviceCommandHistory } from './fleet/DeviceCommandHistory';
import { DeviceRemoteActions } from './fleet/DeviceRemoteActions';

export function FleetPage() {
  const { t } = useTranslation(['displays', 'common']);
  const organisationId = useOrganisationId();
  const [historyDeviceId, setHistoryDeviceId] = useState<string | null>(null);
  const devicesQuery = useQuery({
    queryKey: ['devices', organisationId],
    queryFn: () => api.listDevices(organisationId),
    refetchInterval: 15_000,
  });
  const { isRetrying, retry } = useQueryRetry(() => devicesQuery.refetch());

  const fleet = devicesQuery.data ? deriveFleetCounts(devicesQuery.data) : null;
  const historyDevice = devicesQuery.data?.find((d) => d.id === historyDeviceId);

  return (
    <div className="space-y-6">
      <StatsGrid>
        <StatCard
          label={t('fleet.stats.devices')}
          value={formatCount(fleet?.total, devicesQuery.isPending)}
          note={t('fleet.stats.activeFleet')}
        />
        <StatCard
          label={t('fleet.stats.online')}
          value={formatCount(fleet?.online, devicesQuery.isPending)}
          tone="ok"
        />
        <StatCard
          label={t('fleet.stats.offline')}
          value={formatCount(fleet?.offline, devicesQuery.isPending)}
          note={t('fleet.stats.includesUnknown')}
          tone="bad"
        />
      </StatsGrid>

      <p className="text-sm text-muted">{t('fleet.intro')}</p>

      {devicesQuery.isError || isRetrying ? (
        <QueryErrorState
          message={t('fleet.loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : devicesQuery.isPending ? (
        <QueryLoadingBlock variant="rows" />
      ) : (devicesQuery.data?.length ?? 0) === 0 ? (
        <QueryEmptyState>{t('fleet.empty')}</QueryEmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-panel">
          <table className="w-full text-start text-sm">
            <thead className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-3">{t('fleet.table.device')}</th>
                <th className="px-4 py-3">{t('fleet.table.status')}</th>
                <th className="px-4 py-3">{t('fleet.table.display')}</th>
                <th className="px-4 py-3">{t('fleet.table.playlistVersion')}</th>
                <th className="px-4 py-3">{t('fleet.table.lastSeen')}</th>
                <th className="px-4 py-3">{t('fleet.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {devicesQuery.data?.map((device) => (
                <DeviceRow
                  key={device.id}
                  organisationId={organisationId}
                  device={device}
                  onShowHistory={setHistoryDeviceId}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyDevice ? (
        <div className="rounded-xl border border-line bg-panel p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-base font-bold text-ink">
              {t('fleet.history.title', { code: historyDevice.deviceCode })}
            </h2>
            <button
              type="button"
              onClick={() => setHistoryDeviceId(null)}
              className="text-sm font-semibold text-muted hover:text-ink"
            >
              {t('common:actions.close')}
            </button>
          </div>
          <DeviceCommandHistory organisationId={organisationId} deviceId={historyDevice.id} />
        </div>
      ) : null}
    </div>
  );
}

function DeviceRow({
  organisationId,
  device,
  onShowHistory,
}: {
  organisationId: string;
  device: DeviceSummary;
  onShowHistory: (deviceId: string) => void;
}) {
  const { t } = useTranslation('displays');
  const queryClient = useQueryClient();
  const [revokeMessage, setRevokeMessage] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['devices', organisationId] });

  const revoke = useMutation({
    mutationFn: () => api.revokeDevice(organisationId, device.id),
    onSuccess: () => {
      setRevokeMessage(t('fleet.revokedMessage'));
      invalidate();
    },
    onError: (err) => setRevokeMessage(apiErrorMessage(err, t('fleet.revokeFailed'))),
  });

  const hardware = formatDeviceHardware(device.manufacturer, device.model);
  const connectionStatus =
    device.status === 'revoked'
      ? null
      : getScreenConnectionStatus({ lastSeenAt: device.lastHeartbeatAt });

  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-4 py-3">
        <p className="font-semibold text-ink">{device.deviceCode}</p>
        <p className="text-xs text-muted">{device.appVersion ?? t('fleet.unknownVersion')}</p>
        {hardware ? <p className="mt-0.5 text-xs text-muted">{hardware}</p> : null}
        {device.platform ? (
          <div className="mt-1.5">
            <PlatformPill platform={device.platform} />
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        {connectionStatus ? (
          <StatusPill status={connectionStatus} />
        ) : (
          <span className="text-xs font-semibold text-bad">{t('fleet.revoked')}</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-muted">
        {formatDeviceDisplaySummary(device) ?? '—'}
      </td>
      <td className="px-4 py-3 text-ink">{device.currentPlaylistVersion ?? '—'}</td>
      <td className="px-4 py-3 text-muted">{formatRelativeTime(device.lastHeartbeatAt)}</td>
      <td className="px-4 py-3">
        <div className="space-y-2">
          <DeviceRemoteActions
            organisationId={organisationId}
            device={device}
            onEnqueued={() =>
              queryClient.invalidateQueries({
                queryKey: ['device-command-history', organisationId, device.id],
              })
            }
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onShowHistory(device.id)}
              className="rounded-lg border border-line px-2 py-1 text-xs font-semibold text-ink"
            >
              {t('fleet.historyButton')}
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(t('fleet.confirm.revoke', { code: device.deviceCode }))) revoke.mutate();
              }}
              disabled={device.status === 'revoked' || revoke.isPending}
              className="rounded-lg border border-bad/40 px-2 py-1 text-xs font-semibold text-bad disabled:opacity-40"
            >
              {t('fleet.revoke')}
            </button>
          </div>
          {revokeMessage && <p className="text-xs text-muted">{revokeMessage}</p>}
        </div>
      </td>
    </tr>
  );
}
